import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { isEmpty } from 'lodash';
import { Checkbox, Col, Form, Input, notification, Row, Select } from 'antd';
import {
  useAddressQuery,
  useDefaultEmptyFieldError,
  useDefaultMaxLengthFieldError,
} from '@/hooks';
import { applyMask } from '@/utils/mask';
import { cepMask } from '@/utils/mask/commonMasks';
import ZipFormField from '../ZipFormField';
import FloatingFormItem from '../FloatingFormItem';
import { AddressDefaultType, AddressFormProps } from './types';

const AddressForm = ({
  form,
  address,
  simpleForm,
  removeDescription,
  isEmailCrm,
  isRequired = false,
  fieldsDisabled = false,
  removeDelivery,
  removeMain,
  ...props
}: AddressFormProps) => {
  const intl = useIntl();
  const defaultEmptyFieldError = useDefaultEmptyFieldError(isRequired);
  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();

  const { state, city, zipCode } = useAddressQuery();
  const zipCodeInput = Form.useWatch('zipCode', form);

  const [disabled, setDisable] = useState(true);
  const [stateDisabled, setStateDisabled] = useState<boolean>();
  const [cityDisabled, setCityDisabled] = useState<boolean>();

  useEffect(() => {
    if (zipCode.data && !isEmpty(zipCode.data)) {
      if (!zipCode.data.state) {
        setStateDisabled(false);
        setCityDisabled(false);

        notification.error({
          message: 'CEP inválido',
          description:
            'O CEP informado não foi encontrado. Verifique e tente novamente.',
        });
      } else {
        setStateDisabled(true);
        setCityDisabled(true);
      }

      state.setSelectedStateCode(zipCode.data.state);

      setDisable(false);

      form.setFieldsValue({
        cityId: zipCode.data.cityId,
        street: zipCode.data.street,
        city: zipCode.data.cityName,
        district: zipCode.data.district,
        state: zipCode.data.state,
      });
    }
  }, [zipCode.data]);

  useEffect(() => {
    if (zipCodeInput && zipCodeInput.length < 8) {
      setDisable(true);

      form.setFieldsValue({
        cityId: undefined,
        street: undefined,
        city: undefined,
        district: undefined,
        state: undefined,
        number: undefined,
        complement: undefined,
      });
    } else if (zipCodeInput !== applyMask(address?.zipCode, cepMask)) {
      zipCode.setSelectedZipCode(zipCodeInput);
    } else {
      setDisable(false);
    }
  }, [zipCodeInput]);

  useEffect(() => {
    if (address) {
      city.setSelectedCityId(address.cityId);

      setStateDisabled(true);
      setCityDisabled(true);

      form.setFieldsValue({
        ...address,
        zipCode: applyMask(address.zipCode, cepMask),
      });
    }
  }, [address]);

  useEffect(() => {
    if (city.selectedCity) {
      state.setSelectedStateCode(city.selectedCity.state);

      form.setFieldsValue({
        cityId: city.selectedCity.id,
        state: city.selectedCity.state,
        city: city.selectedCity.name,
      });
    }
  }, [city.selectedCity]);

  const handleFormValuesChange = (valuesChanged: AddressDefaultType) => {
    handleZipChange(valuesChanged);
    handleStateChange(valuesChanged);
  };

  const handleZipChange = (valuesChanged: AddressDefaultType) => {
    if (!valuesChanged?.zipCode || valuesChanged?.zipCode.length !== 8) {
      zipCode.setSelectedZipCode(undefined);
      return;
    }

    zipCode.setSelectedZipCode(valuesChanged.zipCode);
  };

  const handleStateChange = (valuesChanged: AddressDefaultType) => {
    if (!valuesChanged?.state) return;

    form.resetFields(['cityId']);

    state.setSelectedStateCode(valuesChanged.state);
  };

  return (
    <Form form={form} onValuesChange={handleFormValuesChange} preserve={false}>
      <Row gutter={[16, 16]}>
        <Col span={24} sm={6}>
          <ZipFormField
            disable={fieldsDisabled}
            required={isRequired}
            fieldName="zipCode"
            label={intl.formatMessage({
              id: 'insurers.addresses.form.zipCode',
            })}
          />
        </Col>

        <Col span={24} sm={9}>
          <FloatingFormItem
            name="state"
            label={intl.formatMessage({
              id: 'insurers.addresses.form.id',
            })}
            rules={[defaultEmptyFieldError]}
          >
            <Select
              disabled={stateDisabled || fieldsDisabled}
              allowClear
              showSearch
              loading={state.isFetching}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label as string)
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              options={state.list.map((s) => ({
                value: s.code,
                label: s.code,
              }))}
            />
          </FloatingFormItem>
        </Col>

        <Form.Item hidden name="city" />

        <Col span={24} sm={9}>
          <FloatingFormItem
            name="cityId"
            label={intl.formatMessage({
              id: 'insurers.addresses.form.cityId',
            })}
            rules={[defaultEmptyFieldError]}
          >
            <Select
              disabled={cityDisabled || fieldsDisabled}
              allowClear
              showSearch
              loading={city.isFetching}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label as string)
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              options={city.list.map((c) => ({
                value: c.id,
                label: c.name,
              }))}
              onChange={city.setSelectedCityId}
            />
          </FloatingFormItem>
        </Col>

        {!isEmailCrm && (
          <Col span={24} sm={9}>
            <FloatingFormItem
              name="district"
              label={intl.formatMessage({
                id: 'insurers.addresses.form.district',
              })}
              rules={[defaultEmptyFieldError, defaultMaxLenghtFieldError(100)]}
            >
              <Input disabled={disabled || fieldsDisabled} />
            </FloatingFormItem>
          </Col>
        )}

        {!isEmailCrm && (
          <Col span={24} sm={isEmailCrm ? 24 : 11}>
            <FloatingFormItem
              name="street"
              label={intl.formatMessage({
                id: 'insurers.addresses.form.street',
              })}
              rules={[defaultEmptyFieldError, defaultMaxLenghtFieldError(100)]}
            >
              <Input disabled={disabled || fieldsDisabled} />
            </FloatingFormItem>
          </Col>
        )}

        {!isEmailCrm && (
          <>
            <Col span={24} sm={4}>
              <FloatingFormItem
                name="number"
                label={intl.formatMessage({
                  id: 'insurers.addresses.form.number',
                })}
                rules={[
                  defaultEmptyFieldError,
                  {
                    pattern: /^[0-9]/,
                    message: intl.formatMessage({
                      id: 'commons.form.errors.invalidField',
                    }),
                  },
                ]}
              >
                <Input disabled={fieldsDisabled} />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={simpleForm ? 24 : 10}>
              <FloatingFormItem
                name="complement"
                label={intl.formatMessage({
                  id: 'insurers.addresses.form.complement',
                })}
                rules={[defaultMaxLenghtFieldError(100)]}
              >
                <Input disabled={fieldsDisabled} />
              </FloatingFormItem>
            </Col>
          </>
        )}

        {(!simpleForm || !isEmailCrm) && (
          <>
            {!removeDelivery && (
              <Col span={24} sm={7}>
                <Form.Item
                  name="delivery"
                  initialValue={false}
                  valuePropName="checked"
                >
                  <Checkbox
                    style={{ marginTop: '12px' }}
                    disabled={fieldsDisabled}
                  >
                    {intl.formatMessage({
                      id: 'insurers.addresses.form.delivery',
                    })}
                  </Checkbox>
                </Form.Item>
              </Col>
            )}

            {!removeMain && (
              <Col span={24} sm={7}>
                <Form.Item
                  name="main"
                  initialValue={!props.hasAddress}
                  valuePropName="checked"
                >
                  <Checkbox
                    style={{ marginTop: '12px' }}
                    disabled={fieldsDisabled}
                  >
                    {intl.formatMessage({
                      id: 'insurers.addresses.form.main',
                    })}
                  </Checkbox>
                </Form.Item>
              </Col>
            )}

            {!removeDescription && (
              <Col span={24}>
                <FloatingFormItem
                  name="description"
                  label={intl.formatMessage({
                    id: 'insurers.addresses.form.description',
                  })}
                  rules={[defaultMaxLenghtFieldError(100)]}
                >
                  <Input disabled={fieldsDisabled} />
                </FloatingFormItem>
              </Col>
            )}
          </>
        )}
      </Row>
    </Form>
  );
};

export default AddressForm;
